import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuthStore } from "@/store/auth.store";
import { useTranslation } from "@/hooks/useTranslation";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Return to the page the 401 handler bounced us from (?from=…), if any.
      // Only accept internal paths to avoid open-redirects; "//" would be
      // protocol-relative and escape the app, so reject it.
      const from = searchParams.get("from");
      const target =
        from && from.startsWith("/") && !from.startsWith("//") ? from : "/dashboard";
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, var(--bg-card) 0%, var(--bg-page) 70%)",
      }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
        }}
      >
        <div className="text-center mb-8">
          <h1
            className="font-mono text-2xl font-medium tracking-[3px]"
            style={{ color: "var(--text-primary)" }}
          >
            KIBORG
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {t("auth.applicationRiskIntelligence")}
          </p>
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded text-sm"
            style={{
              background: "var(--danger-bg)",
              color: "var(--danger)",
              border: "1px solid var(--danger)",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="login-email"
              className="block text-sm font-medium mb-1"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("auth.email")}
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@kiborg.io"
              required
              className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
              style={{
                background: "var(--bg-input)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-default)",
                ["--tw-ring-color" as string]: "var(--accent)",
              }}
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block text-sm font-medium mb-1"
              style={{ color: "var(--text-secondary)" }}
            >
              {t("auth.password")}
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.enterPassword")}
                required
                className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
                style={{
                  background: "var(--bg-input)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-default)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: "var(--text-muted)" }}
              >
                {showPassword ? t("auth.hide") : t("auth.show")}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded-md text-sm font-medium transition-colors"
            style={{
              background: "var(--accent)",
              color: "#fff",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t("auth.signingIn") : t("auth.signIn")}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link
            to="/forgot-password"
            className="text-sm"
            style={{ color: "var(--accent)" }}
          >
            {t("auth.forgotPassword")}
          </Link>
        </div>
      </div>
    </div>
  );
}
